import { Link } from "react-router-dom";
import { AlertTriangle, ChevronRight, LineChart, PowerOff, Smartphone } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { QueryErrorCard } from "@/components/QueryErrorCard";
import { StaleDataMarker } from "@/components/ConnectionBadge";
import { EmptyState } from "@/components/fleet/EmptyState";
import { DeviceHealthPill } from "@/components/fleet/DeviceHealthPill";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Stat } from "@/components/ui/Stat";
import { Skeleton } from "@/components/ui/Skeleton";
import { useNow } from "@/components/ClockProvider";
import { useFleetSummary } from "@/hooks/useFleetSummary";
import { S, T } from "@/lib/strings";
import { formatPercent, formatTokens, timeAgo } from "@/lib/utils";
import { qsFromRunFilter } from "@/lib/runFilters";
import type { FailureGroup, OverviewPayload } from "@/types/fleet";

/** The offline list is a prompt to act, not an inventory. Five is plenty. */
const OFFLINE_LIST_CAP = 5;

export function Overview() {
  const { overview, devices, runs, isPending, isError, error, refetch } = useFleetSummary();

  return (
    <>
      <PageHeader
        title={S.overviewTitle}
        hint={
          <>
            {S.overviewHint}
            <StaleDataMarker className="ml-1" />
          </>
        }
      />

      {isError && <QueryErrorCard error={error} onRetry={() => void refetch()} className="mb-5" />}

      {/* ---- row 1: the four numbers ---- */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {isPending || !devices || !runs || !overview ? (
          [0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-[92px]" />)
        ) : (
          <>
            <Stat
              label={S.statDevicesOnline}
              value={
                <span className="tabular-nums">
                  {devices.online}
                  <span className="text-[var(--color-fg-subtle)]">/{devices.total}</span>
                </span>
              }
              hint={
                devices.stale + devices.offline > 0 ? (
                  <Link to="/devices?health=offline" className="hover:text-[var(--color-fg)]">
                    {T.deviceHint(devices.stale, devices.offline)}
                  </Link>
                ) : (
                  "全部在線"
                )
              }
            />

            <Stat
              label={S.statRunning}
              value={<span className="tabular-nums">{runs.running}</span>}
              hint={
                <Link to="/runs?status=RUNNING" className="hover:text-[var(--color-fg)]">
                  {S.viewAll}
                </Link>
              }
            />

            <Stat
              label={S.statSuccessRateToday}
              value={
                <span className="tabular-nums">
                  {runs.today_total === 0
                    ? "—"
                    : formatPercent(runs.today_success_rate ?? runs.today_success / runs.today_total, 0)}
                </span>
              }
              hint={T.todayCounts(runs.today_success, runs.today_failed)}
            />

            <Stat
              label={S.statTokensToday}
              value={
                <span className="tabular-nums">
                  {formatTokens(
                    overview.today?.tokens?.total ?? 0,
                  )}
                </span>
              }
              hint={`prompt ${formatTokens(overview.today?.tokens?.prompt ?? 0)} · completion ${formatTokens(
                overview.today?.tokens?.completion ?? 0,
              )}`}
            />
          </>
        )}
      </div>

      {/* ---- row 2: the actionable panel ---- */}
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-[2fr_1fr]">
        <FailurePanel
          groups={overview?.failures_24h ?? []}
          loading={isPending}
        />
        <OfflinePanel devices={overview?.quiet_devices ?? []} loading={isPending} />
      </div>

      {/* ---- phase 2 ---- */}
      <div className="mt-3">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-start gap-2.5">
              <LineChart size={15} className="mt-0.5 shrink-0 text-[var(--color-fg-faint)]" />
              <div>
                <div className="text-sm font-medium text-[var(--color-fg-subtle)]">
                  {S.phase2ChartsTitle}
                </div>
                <p className="mt-0.5 text-xs text-[var(--color-fg-faint)]">{S.phase2ChartsHint}</p>
              </div>
            </div>
            {/* Charts reserve their height now so the page does not reflow when
                phase 2 fills them in. */}
            <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="h-[160px] rounded-lg border border-dashed border-[var(--color-border)]" />
              <div className="h-[160px] rounded-lg border border-dashed border-[var(--color-border)]" />
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

/* ------------------------------------------------------------------ *
 * Failures — a list, not a chart
 * ------------------------------------------------------------------ */

function FailurePanel({ groups, loading }: { groups: FailureGroup[]; loading: boolean }) {
  return (
    <Card>
      <CardHeader className="flex items-baseline justify-between gap-3">
        <CardTitle>{S.failures24h}</CardTitle>
        <Link
          to="/runs?status=FAILED&date=24h"
          className="text-[11px] text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]"
        >
          {S.viewAll}
        </Link>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-1.5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-[46px] w-full" />
            ))}
          </div>
        ) : groups.length === 0 ? (
          <EmptyState
            title={S.noFailures}
            body="所有 run 都成功了。這裡出現項目時，每一行都可以直接點進去看是哪些機、哪個任務出事。"
            compact
          />
        ) : (
          <ul className="space-y-1">
            {groups.map((g) => (
              <li key={`${g.error}-${g.task_id ?? "all"}`}>
                <Link
                  to={`/runs${qsFromRunFilter({
                    status: "FAILED",
                    has_error: true,
                    task_id: g.task_id ?? undefined,
                    date: "24h",
                    q: g.error,
                  })}`}
                  className="flex items-center gap-2.5 rounded-md border border-[var(--color-border-subtle)] bg-[rgba(255,255,255,0.02)] px-3 py-2 transition-colors hover:bg-[rgba(255,255,255,0.05)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
                >
                  <AlertTriangle size={13} className="shrink-0 text-[#f59e0b]" />
                  <span className="font-mono text-[13px] font-medium text-[var(--color-fg)]">
                    {T.failureGroup(g.count, g.error)}
                  </span>
                  {g.task_name && (
                    <>
                      <span className="text-[var(--color-fg-faint)]">·</span>
                      <span className="truncate text-[12px] text-[var(--color-fg-muted)]">
                        {T.failureTask(g.task_name)}
                      </span>
                    </>
                  )}
                  <span className="text-[var(--color-fg-faint)]">·</span>
                  <span className="whitespace-nowrap text-[12px] text-[var(--color-fg-subtle)]">
                    {T.failureAffects(g.devices)}
                  </span>
                  <ChevronRight size={13} className="ml-auto shrink-0 text-[var(--color-fg-faint)]" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

/* ------------------------------------------------------------------ *
 * Offline devices
 * ------------------------------------------------------------------ */

function OfflinePanel({
  devices,
  loading,
}: {
  devices: OverviewPayload["quiet_devices"];
  loading: boolean;
}) {
  const now = useNow();
  const shown = devices.slice(0, OFFLINE_LIST_CAP);
  const rest = devices.length - shown.length;

  return (
    <Card>
      <CardHeader className="flex items-baseline justify-between gap-3">
        <CardTitle>{S.offlineDevices}</CardTitle>
        {devices.length > 0 && (
          <Link
            to="/devices?health=offline"
            className="text-[11px] text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)]"
          >
            {S.viewAll}
          </Link>
        )}
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="space-y-1.5">
            {[0, 1, 2].map((i) => (
              <Skeleton key={i} className="h-8 w-full" />
            ))}
          </div>
        ) : shown.length === 0 ? (
          <EmptyState
            icon={Smartphone}
            title={S.noOfflineDevices}
            body="失去連線的裝置會排在這裡，最舊的在前，方便先處理。"
            compact
          />
        ) : (
          <>
            <ul className="space-y-0.5">
              {shown.map((d) => (
                <li key={d.id}>
                  <Link
                    to={`/devices/${d.id}`}
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-[rgba(255,255,255,0.04)] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-accent)]"
                  >
                    <PowerOff size={12} className="shrink-0 text-[var(--color-fg-faint)]" />
                    <span className="truncate text-[13px] text-[var(--color-fg)]">{d.label}</span>
                    <span className="ml-auto whitespace-nowrap text-[11px] text-[var(--color-fg-faint)]">
                      {timeAgo(d.last_seen_at, now)}
                    </span>
                    <DeviceHealthPill device={d} size="sm" />
                  </Link>
                </li>
              ))}
            </ul>
            {rest > 0 && (
              <Link
                to="/devices?health=offline"
                className="mt-1.5 block px-2 text-[11px] text-[var(--color-accent)] hover:underline"
              >
                另有 {rest} 部 · {S.viewAll}
              </Link>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
